import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/ImageGallery.css';
import { Button, IconButton, SourceBadge, Spinner } from './';
import { ChevronWide, Exit } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { buildSourceLookup, resolveImageSource } from '../utils/imageSourceUtils';
import { resolveSubRoomBadge } from '../utils/imageLabelUtils';

function ImageGallery(props) {
    const images = props.images;
    const imageIndex = props.imageIndex;
    const setImageIndex = props.setImageIndex;
    const closeImageGallery = props.closeImageGallery;
    const alt = props.alt;
    const sourceLookup = useMemo(() => buildSourceLookup(props.imageSources), [props.imageSources]);
    const sourceFavicons = props.sourceFavicons || null;
    // mlsPhotoUrl bridge for the labeled branch (firebaseUrl → originalUrl).
    const labelByUrl = useMemo(() => {
        const m = new Map();
        for (const label of (props.imageLabels?.labels || [])) {
            const fb = typeof label?.firebaseUrl === 'string' ? label.firebaseUrl.trim() : '';
            if (fb && !m.has(fb)) m.set(fb, label);
        }
        return m;
    }, [props.imageLabels]);
    const failedImagesRef = useRef(new Set());
    const [failedCount, setFailedCount] = useState(0);
    const imgRef = useRef(null);
    const loadTimerRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const clearLoadTimer = useCallback(() => {
        if (loadTimerRef.current) {
            clearTimeout(loadTimerRef.current);
            loadTimerRef.current = null;
        }
    }, []);

    const handleImageLoad = useCallback(() => {
        clearLoadTimer();
        setLoading(false);
    }, [clearLoadTimer]);

    // When the index changes, the <img> keeps painting the previous src until
    // the new one decodes. If the new src is already cached, show it instantly.
    // Otherwise reveal a spinner only after a short grace period, so preloaded
    // / fast frames swap in without ever flashing a loading state.
    useEffect(() => {
        clearLoadTimer();
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth > 0) {
            setLoading(false);
            return;
        }
        loadTimerRef.current = setTimeout(() => setLoading(true), 120);
        return clearLoadTimer;
    }, [imageIndex, clearLoadTimer]);

    // Preload neighbouring images so arrow-key navigation lands on a cached
    // frame. Covers two in each direction to keep up with rapid key presses.
    useEffect(() => {
        if (!Array.isArray(images) || images.length <= 1) return;
        for (const offset of [1, -1, 2, -2]) {
            const idx = ((imageIndex + offset) % images.length + images.length) % images.length;
            const src = images[idx];
            if (typeof src === 'string' && src) {
                const preloader = new Image();
                preloader.src = src;
            }
        }
    }, [imageIndex, images]);

    const findNextValidIndex = useCallback((from, direction) => {
        const len = images.length;
        let idx = ((from + direction) % len + len) % len;
        let checked = 0;
        while (failedImagesRef.current.has(idx) && checked < len) {
            idx = ((idx + direction) % len + len) % len;
            checked++;
        }
        return checked >= len ? -1 : idx;
    }, [images.length]);

    const handleImageError = () => {
        clearLoadTimer();
        setLoading(false);
        failedImagesRef.current.add(imageIndex);
        setFailedCount(failedImagesRef.current.size);
        // If all images failed, close the gallery
        if (failedImagesRef.current.size >= images.length) {
            closeImageGallery();
            return;
        }
        const next = findNextValidIndex(imageIndex, 1);
        if (next === -1) {
            closeImageGallery();
            return;
        }
        setImageIndex(next);
    };

    useEffect(() => {
        const toggleArrowEffect = (forward) => {
            const direction = forward ? 1 : -1;
            const next = findNextValidIndex(imageIndex, direction);
            if (next !== -1) setImageIndex(next);
        };
        
        const handleKeyDown = (event) => {
            if ( event.key === 'ArrowRight' && images.length > 1 ) {
                toggleArrowEffect(true);
            } else if ( event.key === 'ArrowLeft' && images.length > 1 ) {
                toggleArrowEffect(false);
            }
            else if ( event.key === 'Escape' ) {
                // The gallery is usually layered over the ScrollableImages grid, which
                // listens for Escape too. Swallow the key so only the top layer closes —
                // Escape must land you back on the grid, exactly like the Close button.
                event.stopPropagation();
                closeImageGallery();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [imageIndex, images, setImageIndex, closeImageGallery, findNextValidIndex]);

    const toggleArrow = (forward) => {
        const direction = forward ? 1 : -1;
        const next = findNextValidIndex(imageIndex, direction);
        if (next !== -1) setImageIndex(next);
    };

    return (
        <div className="image-gallery-outer-container">
            <div className="image-gallery-inner-container relative-container">
                <div className="image-gallery-close-button-container">
                    <Button
                        buttonType="light-button"
                        size="large"
                        text={"Close"}
                        cta={() => closeImageGallery()}
                        disabled={false}
                        leftIcon={<Exit className="" fill={colour.gray06} size={16} clickFunc={null} />}
                        leftIconClass=""
                        rightIcon={null}
                        rightIconClass=""
                        alt=""
                        standardIcon={false}
                        badge={false}
                        badgeVal={null}
                        badgeBG={null}
                        buttonRef={null}
                        spinner={false}
                    />
                </div>
                {
                    (images.length - failedCount) > 1 &&
                    <span className="heading-small-regular colour-white">
                        {images.slice(0, imageIndex + 1).filter((_, i) => !failedImagesRef.current.has(i)).length} / {images.length - failedCount}
                    </span>
                }
                {
                    (images.length - failedCount) > 1 &&
                    <IconButton
                        size="large"
                        icon={<ChevronWide fill={colour.gray06} className="image-gallery-arrow rotate-90" />}
                        disabled={false}
                        clickFunc={() => toggleArrow(false)}
                        standardIcon={false}
                        background={true}
                        bgType={"white"}
                        contextTooltip={false}
                        contextTooltipBG={null}
                        contextTooltipText={null}
                        customClass={"image-gallery-arrow-element image-gallery-arrow-element-left"}
                    />
                }
                {
                    (images.length - failedCount) > 1 &&
                    <IconButton
                        size="large"
                        icon={<ChevronWide fill={colour.gray06} className="image-gallery-arrow rotate-90-negative" />}
                        disabled={false}
                        clickFunc={() => toggleArrow(true)}
                        standardIcon={false}
                        background={true}
                        bgType={"white"}
                        contextTooltip={false}
                        contextTooltipBG={null}
                        contextTooltipText={null}
                        customClass={"image-gallery-arrow-element image-gallery-arrow-element-right"}
                    />
                }
                {/* Figure = image + optional caption stacked vertically, centred as
                    one block. The caption sits BELOW the image (normal flow) so it
                    never hides image content — e.g. the sqft breakdown printed at the
                    bottom of a floor plan. The badges stay absolute over the image. */}
                <div className="image-gallery-figure">
                    <div className="image-gallery-element-wrapper relative-container">
                        {loading &&
                            <div className="image-gallery-spinner-container">
                                <Spinner className="spinner-lg" />
                            </div>
                        }
                        <img
                            ref={imgRef}
                            src={images[imageIndex]}
                            className={"image-gallery-element cursor-pointer" + (loading ? " image-gallery-element-loading" : "") + (props.caption ? " image-gallery-element-captioned" : "")}
                            alt={alt}
                            onClick={() => toggleArrow(true)}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                        />
                        <SourceBadge source={resolveImageSource(sourceLookup, images[imageIndex], labelByUrl.get(typeof images[imageIndex] === 'string' ? images[imageIndex].trim() : ''))} sourceFavicons={sourceFavicons} />
                        {/* Per-photo room badge — the floor plan's printed name for this room
                            ("Dining Area"), else the basement BEDROOM marker. Same resolver the
                            grid uses, so a photo reads the same opened as it does in its section.
                            Renders only when the photo carries one. */}
                        {(() => {
                            const cur = typeof images[imageIndex] === 'string' ? images[imageIndex].trim() : '';
                            const text = resolveSubRoomBadge(labelByUrl.get(cur));
                            if (!text) return null;
                            return <span className="image-gallery-subroom label-bold">{text}</span>;
                        })()}
                    </div>
                    {/* Optional caption — rendered below the image. Callers that don't
                        pass `caption` render exactly as before. */}
                    {props.caption && (
                        <div className="image-gallery-caption">
                            {props.caption}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
};

export default ImageGallery;